/**
 * Calvary Building Services — live site config (photos + accent color only).
 *
 * The admin CMS for this site is intentionally light: admins swap *imagery*, one
 * accent color, the homepage headline stats, and the client reviews. The rest of
 * the copy lives in the components / lib/site.ts. So this config carries just:
 *   1. `secondaryColor` — the gold accent (recolors every accent via --gold).
 *   2. `images` — a fixed set of named image slots (hero, section photos,
 *      service photos, service-area map) the admin can replace.
 *   3. `gallery` — the Projects page photo grid (add / remove / categorize).
 *   4. `stats` — the homepage "why us" headline stats (value + label each).
 *   5. `reviews` — the client reviews / testimonials (add / edit / remove).
 *
 * The public site fetches `/site-config.json` from its own origin at runtime
 * (CloudFront serves it from the CMS config bucket with a 60s TTL), so admin
 * changes appear within ~1 minute. `defaultConfig` is the fallback baked into
 * the bundle for first paint and for when the network is unavailable.
 *
 * IN LOCKSTEP: the slot list, categories, stat defaults, and safe-* rules here
 * are mirrored in cms/lambda/cms-config/index.mjs (server-side normalize) and
 * seeded by cms/config/site-config.json. Change all three together.
 */

export type ProjectCategory =
  | "Kitchens"
  | "Bathrooms"
  | "Outdoor Living"
  | "Whole-Home"
  | "Design to Build";

export const PROJECT_CATEGORIES: ProjectCategory[] = [
  "Kitchens",
  "Bathrooms",
  "Outdoor Living",
  "Whole-Home",
  "Design to Build",
];

export interface GalleryItem {
  id: string;
  url: string;
  alt: string;
  category: ProjectCategory;
  /** Sort order within the grid (ascending). */
  order: number;
  /** Hidden from the public grid when false (admin can stage without showing). */
  active: boolean;
}

/** One homepage "why us" headline stat: a big value + a short label. */
export interface StatItem {
  /** The headline figure, e.g. "15+" or "200+". */
  value: string;
  /** The caption under it, e.g. "Years building in the Lowcountry". */
  label: string;
}

/** One client review shown in the "Kind words" section. */
export interface ReviewItem {
  id: string;
  quote: string;
  author: string;
  /** e.g. "Mount Pleasant, SC". */
  location: string;
}

/** The fixed set of replaceable image slots on the marketing site. */
export type ImageSlot =
  | "heroHome"
  | "aboutMain"
  | "areaMap"
  | "serviceKitchen"
  | "serviceBathroom"
  | "serviceOutdoor"
  | "serviceWholeHome"
  | "serviceDesignBuild";

/** Display metadata for each slot — drives the CMS Photos tab and alt text. */
export const IMAGE_SLOTS: {
  key: ImageSlot;
  label: string;
  hint: string;
}[] = [
  { key: "heroHome", label: "Homepage hero", hint: "Your signature project photo, beside the homepage headline. Portrait ~1200×1500." },
  { key: "aboutMain", label: "About page photo", hint: "The main photo on the About page." },
  { key: "areaMap", label: "Service-area map", hint: "Map or wide shot on the service-area section. Landscape ~1600×1100." },
  { key: "serviceKitchen", label: "Service — Kitchen Remodeling", hint: "Card photo on the Services page." },
  { key: "serviceBathroom", label: "Service — Bathroom Renovations", hint: "Card photo on the Services page." },
  { key: "serviceOutdoor", label: "Service — Outdoor Living", hint: "Card photo on the Services page." },
  { key: "serviceWholeHome", label: "Service — Whole-Home Renovations", hint: "Card photo on the Services page." },
  { key: "serviceDesignBuild", label: "Service — Design to Build", hint: "Card photo on the Services page." },
];

export interface SiteConfig {
  _version: number;
  _updatedAt: string;
  _updatedBy: string;
  /** Accent ("secondary") color as a hex string (e.g. "#b98f52"). The primary
   *  navy is locked to the brand and is not configurable. */
  secondaryColor: string;
  images: Record<ImageSlot, string>;
  gallery: GalleryItem[];
  /** Homepage "why us" headline stats (edited in the CMS; order = display order). */
  stats: StatItem[];
  /** Client reviews shown in the "Kind words" section (add / edit / remove). */
  reviews: ReviewItem[];
}

/** Default accent color — the Calvary bronze-gold from the design. */
export const DEFAULT_SECONDARY_COLOR = "#b98f52";

export const DEFAULT_IMAGES: Record<ImageSlot, string> = {
  heroHome: "/images/defaults/hero.svg",
  aboutMain: "/images/defaults/about.svg",
  areaMap: "/images/defaults/area-map.svg",
  serviceKitchen: "/images/defaults/kitchen.svg",
  serviceBathroom: "/images/defaults/bathroom.svg",
  serviceOutdoor: "/images/defaults/outdoor.svg",
  serviceWholeHome: "/images/defaults/whole-home.svg",
  serviceDesignBuild: "/images/defaults/design-build.svg",
};

/** Default homepage headline stats — mirrored in the Lambda + seed JSON. */
export const DEFAULT_STATS: StatItem[] = [
  { value: "15+", label: "Years building in the Lowcountry" },
  { value: "200+", label: "Projects delivered start to finish" },
  { value: "100%", label: "Licensed, bonded & insured" },
];

/** Caps so a bad/hostile config can't blow out the fixed-width stat row. */
export const MAX_STATS = 4;
const STAT_VALUE_MAXLEN = 12;
const STAT_LABEL_MAXLEN = 80;

/** Default client reviews — mirrored in the Lambda + seed JSON. */
export const DEFAULT_REVIEWS: ReviewItem[] = [
  {
    id: "seed-review-1",
    quote:
      "Calvary took our dated kitchen down to the studs and delivered exactly what we pictured — on schedule, and without a single surprise on the invoice.",
    author: "Sarah & Tom H.",
    location: "Mount Pleasant, SC",
  },
  {
    id: "seed-review-2",
    quote:
      "The screened porch they built is now the most-used room in our house. Their crew treated our home like it was their own.",
    author: "Michael R.",
    location: "Daniel Island, SC",
  },
  {
    id: "seed-review-3",
    quote:
      "From the first walkthrough to the final punch list, communication was flawless. We have already booked them for our primary bath.",
    author: "Jennifer L.",
    location: "Charleston, SC",
  },
];

/** Review field caps (defense-in-depth against hostile config). */
export const MAX_REVIEWS = 24;
const REVIEW_QUOTE_MAXLEN = 500;
const REVIEW_AUTHOR_MAXLEN = 80;
const REVIEW_LOCATION_MAXLEN = 80;

export const defaultConfig: SiteConfig = {
  _version: 1,
  _updatedAt: "2026-07-09T00:00:00Z",
  _updatedBy: "system",
  secondaryColor: DEFAULT_SECONDARY_COLOR,
  images: { ...DEFAULT_IMAGES },
  gallery: [
    { id: "seed-kitchen", url: "/images/defaults/gallery-kitchen.svg", alt: "Kitchen remodel", category: "Kitchens", order: 0, active: true },
    { id: "seed-bathroom", url: "/images/defaults/gallery-bathroom.svg", alt: "Bathroom renovation", category: "Bathrooms", order: 1, active: true },
    { id: "seed-outdoor", url: "/images/defaults/gallery-outdoor.svg", alt: "Outdoor living space", category: "Outdoor Living", order: 2, active: true },
    { id: "seed-wholehome", url: "/images/defaults/gallery-whole-home.svg", alt: "Whole-home renovation", category: "Whole-Home", order: 3, active: true },
    { id: "seed-designbuild", url: "/images/defaults/gallery-design-build.svg", alt: "Design-to-build project", category: "Design to Build", order: 4, active: true },
  ],
  stats: DEFAULT_STATS.map((s) => ({ ...s })),
  reviews: DEFAULT_REVIEWS.map((r) => ({ ...r })),
};

/**
 * Allow only safe URL forms for values that get fed into `<img src>` / `<a href>`.
 * Returns "" for anything that isn't an http(s) URL or a root-relative path —
 * defense-in-depth, since a compromised admin token can write arbitrary JSON
 * to the CMS config but must not be able to inject `javascript:`/`data:` URLs.
 */
function safeUrl(value: unknown): string {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (trimmed === "") return "";
  if (trimmed.startsWith("/")) return trimmed;
  if (/^https?:/i.test(trimmed)) return trimmed;
  return "";
}

/** Accept only a `#rgb` / `#rrggbb` hex color; "" otherwise (falls back to default). */
function safeColor(value: unknown): string {
  if (typeof value !== "string") return "";
  const t = value.trim();
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(t) ? t : "";
}

/** Trim + length-cap a free-text config value; "" for non-strings. */
function safeText(value: unknown, maxLen: number): string {
  if (typeof value !== "string") return "";
  return value.trim().slice(0, maxLen);
}

/**
 * Coerce the stats array: keep item order, cap count + field lengths. A fixed
 * set (no add/remove in the UI), so we keep blanked items rather than dropping
 * them — the public render skips any card that ends up fully empty. Falls back
 * to DEFAULT_STATS when absent/invalid so the section is never empty.
 */
function normalizeStats(value: unknown): StatItem[] {
  if (!Array.isArray(value) || value.length === 0) {
    return DEFAULT_STATS.map((s) => ({ ...s }));
  }
  return value.slice(0, MAX_STATS).map((s) => ({
    value: safeText((s as Partial<StatItem>)?.value, STAT_VALUE_MAXLEN),
    label: safeText((s as Partial<StatItem>)?.label, STAT_LABEL_MAXLEN),
  }));
}

/**
 * Coerce the reviews array. Unlike stats (a fixed set), reviews are a
 * deletable list: an *absent* `reviews` field falls back to the defaults
 * (migrates old configs), but an explicit array — including an empty one — is
 * respected (so removing every review actually hides the section). Items with
 * no quote are dropped; count + field lengths are capped.
 */
function normalizeReviews(value: unknown): ReviewItem[] {
  if (!Array.isArray(value)) return DEFAULT_REVIEWS.map((r) => ({ ...r }));
  return value
    .slice(0, MAX_REVIEWS)
    .map((r, i): ReviewItem => {
      const item = r as Partial<ReviewItem>;
      return {
        id: typeof item?.id === "string" && item.id ? item.id : `review-${i}`,
        quote: safeText(item?.quote, REVIEW_QUOTE_MAXLEN),
        author: safeText(item?.author, REVIEW_AUTHOR_MAXLEN),
        location: safeText(item?.location, REVIEW_LOCATION_MAXLEN),
      };
    })
    .filter((r) => r.quote !== "");
}

function isProjectCategory(value: unknown): value is ProjectCategory {
  return typeof value === "string" && (PROJECT_CATEGORIES as string[]).includes(value);
}

/**
 * Deep-merge a (possibly partial / stale) stored config onto the defaults so
 * new slots stay safe on older saved config, and unsafe/empty URLs fall back
 * to the baked-in placeholder.
 */
export function normalizeSiteConfig(
  config: Partial<SiteConfig> | null | undefined,
): SiteConfig {
  const incoming = config ?? {};

  const images = {} as Record<ImageSlot, string>;
  for (const { key } of IMAGE_SLOTS) {
    images[key] = safeUrl(incoming.images?.[key]) || DEFAULT_IMAGES[key];
  }

  const gallery: GalleryItem[] = Array.isArray(incoming.gallery)
    ? incoming.gallery
        .map((item, i): GalleryItem | null => {
          const url = safeUrl(item?.url);
          if (!url) return null;
          return {
            id: typeof item?.id === "string" && item.id ? item.id : `item-${i}`,
            url,
            alt: typeof item?.alt === "string" ? item.alt : "",
            category: isProjectCategory(item?.category) ? item.category : "Kitchens",
            order: typeof item?.order === "number" ? item.order : i,
            active: item?.active !== false,
          };
        })
        .filter((x): x is GalleryItem => x !== null)
    : defaultConfig.gallery.map((g) => ({ ...g }));

  return {
    _version: typeof incoming._version === "number" ? incoming._version : defaultConfig._version,
    _updatedAt: typeof incoming._updatedAt === "string" ? incoming._updatedAt : defaultConfig._updatedAt,
    _updatedBy: typeof incoming._updatedBy === "string" ? incoming._updatedBy : defaultConfig._updatedBy,
    secondaryColor: safeColor(incoming.secondaryColor) || defaultConfig.secondaryColor,
    images,
    gallery,
    stats: normalizeStats(incoming.stats),
    reviews: normalizeReviews(incoming.reviews),
  };
}
